import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { message, Spin, Tabs, Space } from 'antd';
import { ApartmentOutlined, HistoryOutlined, CodeOutlined } from '@ant-design/icons';
import { AgentConfig, AgentSummary, EXECUTOR_API } from '../types';
import { useUser } from '../contexts/UserContext';
import {
  FlowNode,
  VisualNodeType,
  generateNodeId,
  createDefaultFlow,
  mapVisualToBackend,
  backendNodesToFlow,
  flowNodesToFlowDefinition,
  flowDefinitionToFlowNodes,
} from '../types/flow';
import { createAgent, updateAgent, createNode, activateAgent, fetchAgents, fetchAgentNodes, saveFlow, fetchFlow } from '../services/api.service';
import FlowCanvas from './FlowCanvas';
import NodeConfigDrawer from './NodeConfigDrawer';
import AgentConfigDrawer from './AgentConfigDrawer';
import NodeToolbar, { AgentStatus } from './NodeToolbar';
import ExecutionHistory from './ExecutionHistory';
import ExecutionPanel from './ExecutionPanel';
import OntologyPanel from './OntologyPanel';

type ActiveTab = 'flow' | 'executions' | 'ontologies' | 'api';

const BuilderPage: React.FC = () => {
  const navigate = useNavigate();
  const { agentId: urlAgentId } = useParams<{ agentId: string }>();
  const { canEdit } = useUser();
  const isEditMode = !!urlAgentId && urlAgentId !== 'new';

  // ── Flow state ──
  const [flowNodes, setFlowNodes] = useState<FlowNode[]>(createDefaultFlow());
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [editingNode, setEditingNode] = useState<FlowNode | null>(null);
  const [loadingAgent, setLoadingAgent] = useState(false);

  // ── Agent config state ──
  const [agentConfig, setAgentConfig] = useState<AgentConfig>({
    model_name: 'gemini-2.5-flash',
    model_provider: 'vertex_ai',
    temperature: 0.7,
    recursion_limit: 25,
    name: 'Nuevo Agente',
    task: '',
    role: '',
    description: '',
    expected_output: '',
    update_kg: false,
  });

  // ── UI state ──
  const [agentDrawerOpen, setAgentDrawerOpen] = useState(false);
  const [nodeDrawerOpen, setNodeDrawerOpen] = useState(false);
  const [status, setStatus] = useState<AgentStatus>(isEditMode ? 'saved' : 'draft');
  const [agentId, setAgentId] = useState<string | null>(isEditMode ? urlAgentId : null);
  const [webhookUrl, setWebhookUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);
  const [isCognitiveAgent, setIsCognitiveAgent] = useState(false);
  const [activeTab, setActiveTab] = useState<ActiveTab>('flow');

  // ── Load existing agent + nodes when in edit mode ──
  useEffect(() => {
    if (!isEditMode || !urlAgentId) return;

    let cancelled = false;

    const loadAgent = async () => {
      setLoadingAgent(true);
      try {
        // Fetch agent details from the agents list
        const agents = await fetchAgents();
        const agent = agents.find((a: AgentSummary) => a.id === urlAgentId);

        if (!agent) {
          message.error('Agente no encontrado');
          navigate('/');
          return;
        }

        if (cancelled) return;

        // Cognitive agents have their own detail page
        if (agent.cognitiveSpec) {
          navigate(`/agents/${urlAgentId}`, { replace: true });
          return;
        }

        // Set agent config from backend data (camelCase → snake_case for AgentConfig)
        setAgentConfig({
          model_name: agent.modelName,
          model_provider: agent.modelProvider,
          temperature: agent.temperature,
          recursion_limit: agent.recursionLimit,
          name: agent.name,
          task: agent.task,
          role: agent.role,
          description: agent.description,
          expected_output: agent.expectedOutput,
          update_kg: agent.updateKg ?? false,
          system_prompt: agent.systemPrompt,
        });

        // Try loading FlowDefinition first (preserves topology), fallback to flat nodes
        const flowResponse = await fetchFlow(urlAgentId);

        if (cancelled) return;

        if (flowResponse?.flow_definition) {
          setFlowNodes(flowDefinitionToFlowNodes(flowResponse.flow_definition));
        } else {
          const backendNodes = await fetchAgentNodes(urlAgentId);
          if (cancelled) return;
          if (backendNodes.length > 0) {
            const visualFlow = backendNodesToFlow(backendNodes);
            setFlowNodes(visualFlow);
          }
        }

        if (agent.cognitiveSpec) {
          setIsCognitiveAgent(true);
          setWebhookUrl(`/agent/cognitive/${urlAgentId}/run`);
          setStatus('active');
        } else {
          setStatus('saved');
        }
      } catch (err) {
        if (!cancelled) {
          const errorMsg = err instanceof Error ? err.message : 'Error desconocido';
          message.error(`Error cargando agente: ${errorMsg}`);
        }
      } finally {
        if (!cancelled) {
          setLoadingAgent(false);
        }
      }
    };

    loadAgent();

    return () => {
      cancelled = true;
    };
  }, [isEditMode, urlAgentId, navigate]);

  // ── Node click → open config drawer or agent drawer ──
  const handleNodeClick = useCallback((node: FlowNode) => {
    setSelectedNodeId(node.id);
    if (node.type === 'start') {
      // Click en nodo Inicio → abrir drawer de configuración del agente
      setAgentDrawerOpen(true);
    } else {
      setEditingNode(node);
      setNodeDrawerOpen(true);
    }
  }, []);

  // ── Update a node in the tree (recursively) ──
  const updateNodeInTree = useCallback(
    (nodes: FlowNode[], nodeId: string, updates: Partial<FlowNode>): FlowNode[] => {
      return nodes.map((n) => {
        if (n.id === nodeId) {
          return { ...n, ...updates };
        }
        if (n.branches) {
          return {
            ...n,
            branches: n.branches.map((branch) =>
              updateNodeInTree(branch, nodeId, updates)
            ),
          };
        }
        return n;
      });
    },
    []
  );

  // ── Save node config from drawer ──
  const handleNodeSave = useCallback(
    async (nodeId: string, updates: Partial<FlowNode>) => {
      const updatedNodes = updateNodeInTree(flowNodes, nodeId, updates);
      setFlowNodes(updatedNodes);
      setNodeDrawerOpen(false);
      setEditingNode(null);

      // Persist flow to backend in edit mode
      if (isEditMode && agentId) {
        try {
          const flowPayload = flowNodesToFlowDefinition(updatedNodes);
          await saveFlow(agentId, flowPayload);
          message.success('Nodo guardado');
        } catch (err) {
          const errorMsg = err instanceof Error ? err.message : 'Error desconocido';
          message.error(`Error guardando nodo: ${errorMsg}`);
        }
      } else {
        message.success('Nodo actualizado');
      }
    },
    [updateNodeInTree, flowNodes, isEditMode, agentId]
  );

  // ── Delete a node from the tree (recursively) ──
  const deleteNodeFromTree = useCallback(
    (nodes: FlowNode[], nodeId: string): FlowNode[] => {
      const filtered = nodes.filter((n) => n.id !== nodeId);
      return filtered.map((n) => {
        if (n.branches) {
          return {
            ...n,
            branches: n.branches.map((branch) =>
              deleteNodeFromTree(branch, nodeId)
            ),
          };
        }
        return n;
      });
    },
    []
  );

  const handleDeleteNode = useCallback(
    async (nodeId: string) => {
      const updatedNodes = deleteNodeFromTree(flowNodes, nodeId);
      setFlowNodes(updatedNodes);
      if (selectedNodeId === nodeId) {
        setSelectedNodeId(null);
      }

      // Persist flow to backend in edit mode
      if (isEditMode && agentId) {
        try {
          const flowPayload = flowNodesToFlowDefinition(updatedNodes);
          await saveFlow(agentId, flowPayload);
          message.success('Nodo eliminado y guardado');
        } catch (err) {
          const errorMsg = err instanceof Error ? err.message : 'Error desconocido';
          message.error(`Error guardando: ${errorMsg}`);
        }
      } else {
        message.info('Nodo eliminado');
      }
    },
    [deleteNodeFromTree, flowNodes, selectedNodeId, isEditMode, agentId]
  );

  // ── Add node after another node in the main sequence ──
  const createNewNode = (type: VisualNodeType): FlowNode => {
    const defaults: Record<VisualNodeType, { label: string; subtitle?: string; config: Record<string, unknown> }> = {
      start: { label: 'Inicio', config: {} },
      llm: { label: 'Nuevo LLM', subtitle: 'gpt-4o', config: { model: 'gpt-4o' } },
      condition: {
        label: 'Condicion',
        subtitle: 'campo equals valor',
        config: { field: '', operator: 'equals', value: '', true_label: 'Si', false_label: 'No' },
      },
      human_input: { label: 'Input Humano', config: { message_text: '' } },
      extraction: { label: 'Extraccion', config: { prompt: '', output_schema: [] } },
      classification: { label: 'Clasificacion', config: { prompt: '', categories: [] } },
      bigquery_save: { label: 'Guardar BigQuery', config: {} },
      lead_enrichment: {
        label: 'Buscar Lead',
        subtitle: 'lookup en MongoDB',
        config: {},
      },
      estimate_budget: {
        label: 'Calcular Presupuesto',
        subtitle: 'cuestionario reforma',
        config: {},
      },
      http_request: {
        label: 'Llamada HTTP',
        subtitle: 'Sin configurar',
        config: {
          method: 'POST',
          url: '',
          headers: {},
          auth_mode: 'none',
          auth_value: '',
          body_template: '',
          timeout: 30,
        },
      },
    };

    const def = defaults[type];
    const newNode: FlowNode = {
      id: generateNodeId(),
      type,
      label: def.label,
      subtitle: def.subtitle,
      config: def.config,
    };

    if (type === 'condition') {
      newNode.branches = [[], []];
    }

    return newNode;
  };

  const addNodeAfter = useCallback(
    (nodes: FlowNode[], afterId: string, newNode: FlowNode): FlowNode[] => {
      const result: FlowNode[] = [];
      for (const n of nodes) {
        result.push(n);
        if (n.id === afterId) {
          result.push(newNode);
        }
        if (n.branches) {
          const idx = result.length - 1;
          result[idx] = {
            ...result[idx],
            branches: n.branches.map((branch) =>
              addNodeAfter(branch, afterId, newNode)
            ),
          };
        }
      }
      return result;
    },
    []
  );

  const handleAddNode = useCallback(
    (type: VisualNodeType, afterNodeId: string) => {
      const newNode = createNewNode(type);
      setFlowNodes((prev) => addNodeAfter(prev, afterNodeId, newNode));
      setEditingNode(newNode);
      setSelectedNodeId(newNode.id);
      setNodeDrawerOpen(true);
    },
    [addNodeAfter]
  );

  // ── Add node to a branch of a condition ──
  const handleAddBranchNode = useCallback(
    (type: VisualNodeType, conditionNodeId: string, branchIndex: number) => {
      const newNode = createNewNode(type);

      setFlowNodes((prev) => {
        const addToBranch = (nodes: FlowNode[]): FlowNode[] => {
          return nodes.map((n) => {
            if (n.id === conditionNodeId && n.branches) {
              const newBranches = n.branches.map((branch, bi) => {
                if (bi === branchIndex) {
                  return [...branch, newNode];
                }
                return branch;
              });
              return { ...n, branches: newBranches };
            }
            if (n.branches) {
              return { ...n, branches: n.branches.map((b) => addToBranch(b)) };
            }
            return n;
          });
        };
        return addToBranch(prev);
      });

      setEditingNode(newNode);
      setSelectedNodeId(newNode.id);
      setNodeDrawerOpen(true);
    },
    []
  );

  // ── Flatten flow nodes for backend (recursive) ──
  const flattenNodes = (nodes: FlowNode[]): FlowNode[] => {
    const result: FlowNode[] = [];
    for (const n of nodes) {
      result.push(n);
      if (n.branches) {
        for (const branch of n.branches) {
          result.push(...flattenNodes(branch));
        }
      }
    }
    return result;
  };

  // ── Save to backend ──
  const handleSave = async () => {
    if (!agentConfig.name || !agentConfig.task || !agentConfig.role || !agentConfig.description || !agentConfig.expected_output) {
      message.warning('Configura los ajustes del agente antes de guardar (nombre, tarea, rol, descripcion, output esperado)');
      setAgentDrawerOpen(true);
      return;
    }

    setSaving(true);

    try {
      if (isEditMode && agentId) {
        // PATCH: update existing agent config + save flow
        await updateAgent(agentId, agentConfig);
        const flowPayload = flowNodesToFlowDefinition(flowNodes);
        await saveFlow(agentId, flowPayload);
        setStatus('saved');
        message.success('Agente actualizado correctamente');
      } else {
        // POST: create new agent + nodes
        const allNodes = flattenNodes(flowNodes);
        const backendNodes = allNodes
          .map(mapVisualToBackend)
          .filter((n): n is NonNullable<typeof n> => n !== null);

        if (backendNodes.length === 0) {
          message.warning('Anade al menos un nodo (LLM, Condicion o Input Humano) antes de guardar');
          setSaving(false);
          return;
        }

        const agentResponse = await createAgent(agentConfig);
        const newAgentId = agentResponse.agent_id;
        setAgentId(newAgentId);

        for (const node of backendNodes) {
          await createNode(newAgentId, {
            type: node.type,
            name: node.name,
            prompt: node.prompt,
            config: node.config,
          });
        }

        // Save FlowDefinition (preserves topology + edges)
        const flowPayload = flowNodesToFlowDefinition(flowNodes);
        await saveFlow(newAgentId, flowPayload);

        setStatus('saved');
        message.success(`Agente guardado con ${backendNodes.length} nodo(s)`);

        // Navigate to edit URL so subsequent saves use PATCH, not POST
        navigate(`/builder/${newAgentId}`, { replace: true });
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Error desconocido';
      message.error(`Error al guardar: ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  // ── Activate agent ──
  const handleActivate = async () => {
    if (!agentId) {
      message.warning('Guarda el agente primero');
      return;
    }

    if (isCognitiveAgent) {
      setWebhookUrl(`/agent/cognitive/${agentId}/run`);
      setStatus('active');
      message.success('Agente cognitivo activo — usa el webhook para ejecutarlo');
      return;
    }

    setActivating(true);

    try {
      const response = await activateAgent(agentId);
      setStatus('active');
      setWebhookUrl(response.webhook_url);
      message.success('Agente activado correctamente');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Error desconocido';
      message.error(`Error al activar: ${errorMsg}`);
    } finally {
      setActivating(false);
    }
  };

  // ── Agent config save ──
  const handleAgentConfigSave = async (config: AgentConfig) => {
    setAgentConfig(config);

    if (isEditMode && agentId) {
      setSaving(true);
      try {
        await updateAgent(agentId, config);
        message.success('Agente actualizado correctamente');
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Error desconocido';
        message.error(`Error al guardar: ${errorMsg}`);
      } finally {
        setSaving(false);
      }
    } else {
      message.success('Configuracion del agente actualizada');
    }
  };

  if (loadingAgent) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fafbfc' }}>
        <Spin size="large" tip="Cargando agente..." />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 50px)', overflow: 'hidden' }}>
      <NodeToolbar
        agentName={agentConfig.name}
        agentId={agentId}
        status={status}
        webhookUrl={webhookUrl}
        saving={saving}
        activating={activating}
        onOpenSettings={() => setAgentDrawerOpen(true)}
        onSave={handleSave}
        onActivate={handleActivate}
        onBack={() => navigate('/')}
        readOnly={!canEdit}
      />

      <div style={{ flex: 1, overflow: 'auto' }}>
        {isEditMode ? (
          <Tabs
            className="agent-detail-tabs"
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key as ActiveTab)}
            tabBarStyle={{ padding: '0 24px', margin: 0 }}
            style={{ height: '100%' }}
            items={[
              {
                key: 'flow',
                label: 'Flow',
                children: (
                  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <div style={{ flex: 1, padding: '0 24px', overflow: 'hidden' }}>
                      <FlowCanvas
                        nodes={flowNodes}
                        selectedNodeId={selectedNodeId}
                        onNodeClick={handleNodeClick}
                        onDeleteNode={handleDeleteNode}
                        onAddNode={handleAddNode}
                        onAddBranchNode={handleAddBranchNode}
                      />
                    </div>
                    <ExecutionPanel
                      agentId={agentId}
                      onViewAll={() => setActiveTab('executions')}
                    />
                  </div>
                ),
              },
              {
                key: 'executions',
                label: (
                  <Space size={4}>
                    <HistoryOutlined />
                    <span>Ejecuciones</span>
                  </Space>
                ),
                children: (
                  <div style={{ padding: '24px' }}>
                    <ExecutionHistory agentId={agentId} />
                  </div>
                ),
              },
              {
                key: 'ontologies',
                label: (
                  <Space size={4}>
                    <ApartmentOutlined />
                    <span>Ontologies</span>
                  </Space>
                ),
                children: <OntologyPanel agentId={agentId} />,
              },
              {
                key: 'api',
                label: (
                  <Space size={4}>
                    <CodeOutlined />
                    <span>API</span>
                  </Space>
                ),
                children: agentId ? (
                  <iframe
                    key={agentId}
                    src={`${EXECUTOR_API}/agent/builder/${agentId}/docs`}
                    title="Agent API Reference"
                    style={{ width: '100%', height: '100%', border: 'none' }}
                  />
                ) : (
                  <div style={{ padding: 24, color: '#8c8c8c' }}>
                    Guarda el agente para ver su API Reference.
                  </div>
                ),
              },
            ]}
          />
        ) : (
          <FlowCanvas
            nodes={flowNodes}
            selectedNodeId={selectedNodeId}
            onNodeClick={handleNodeClick}
            onDeleteNode={handleDeleteNode}
            onAddNode={handleAddNode}
            onAddBranchNode={handleAddBranchNode}
          />
        )}
      </div>

      <NodeConfigDrawer
        node={editingNode}
        open={nodeDrawerOpen}
        onClose={() => {
          setNodeDrawerOpen(false);
          setEditingNode(null);
        }}
        onSave={handleNodeSave}
      />

      <AgentConfigDrawer
        open={agentDrawerOpen}
        onClose={() => setAgentDrawerOpen(false)}
        config={agentConfig}
        onSave={handleAgentConfigSave}
      />

    </div>
  );
};

export default BuilderPage;
